import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Spin,
  DatePicker,
  Divider
} from 'antd';
import {
  graphsDateRangeAtom,
  notificationsAtom,
  userAtom
} from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import { H1 } from '../../../shared-components/typography/Title';
import Card from '../../../shared-components/container/Card';
import {
  getDatePresets,
  thousand,
  toCurrency,
} from '../../../../functions';
import api from '../../../../api/api';
import BatteryBrandsGraph from './shared-components/BatteryBrandsGraph';
import TopSettersGraph from './shared-components/TopSettersGraph';
import TopClosersGraph from './shared-components/TopClosersGraph';
import PayoutReport from './shared-components/PayoutReport';
import DealTypesGraph from './shared-components/DealTypesGraph';

function MeDashboard({ includeBack }) {
  const { RangePicker } = DatePicker;
  const history = useHistory();
  const { t } = useTranslation();
  const [graphsDateRange, setGraphsDateRange] = useRecoilState(graphsDateRangeAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const user = useRecoilValue(userAtom);

  function getSummary() {
    setLoading(true);
    const url = `reports/summary/closer?userId=${user?.id}`;
    api.get(url)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSummary(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  useEffect(() => {
    setGraphsDateRange({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('week').format('MMM DD, YYYY')
    });
    getSummary();
  }, []);

  return (
    <MainPage includeBack requiresCloserAdmin>
      <Spin spinning={loading}>
        <H1 text={t('dashboard')} />
        <div className="flex mb-2 items-center space-x-2">
          <RangePicker
            className="bg-transparent ml-auto"
            presets={getDatePresets(t)}
            value={[
              dayjs(graphsDateRange?.start),
              dayjs(graphsDateRange?.end)
            ]}
            onChange={(obj) => {
              setGraphsDateRange({
                start: obj[0],
                end: obj[1],
                reload: true
              });
            }}
            format="MMM DD, YYYY"
          />
          <Button
            type="primary"
            size="small"
            onClick={() => setGraphsDateRange({
              ...graphsDateRange,
              reload: true
            })}
          >
            {t('refresh')}
          </Button>
        </div>
        <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('no_appointment')}
              description={thousand(summary?.totalLeadsNoAppointment)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('deals')}
              description={thousand(summary?.totalDeals)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('installs')}
              description={thousand(summary?.totalInstalls)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('total_sytem_size')}
              description={thousand(summary?.systemSizeTotal)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('epc_total')}
              description={toCurrency(summary?.epcTotal)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('closer_commision_total')}
              description={toCurrency(summary?.closerCommisionTotal)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('average_epc')}
              description={toCurrency(summary?.averageEpc)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('average_system_size')}
              description={thousand(summary?.averageSystemSize)}
            />
          </Spin>
          <Spin spinning={loading}>
            <Card
              className="col-span-1 w-full border-4 rounded-sm"
              title={t('average_closer_commision')}
              description={toCurrency(summary?.averageCloserCommision)}
            />
          </Spin>
        </div>
        <div className="grid grid-cols-1 mt-4 md:grid-cols-2 gap-4 mb-16">
          <div className="col-span-1 mt-16">
            <BatteryBrandsGraph closerId={user?.id} />
          </div>
          <div className="col-span-1 mt-16">
            <DealTypesGraph closerId={user?.id} />
          </div>
        </div>
      </Spin>
    </MainPage>
  );
}

export default MeDashboard;
